import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';
import { CartApiError, add } from '@theme/bt-cart-api';

const DEFAULT_OPTION_VALUE = 'default title';

const NOTIFY_OPTIMISTIC_MS = 400;

export class BtCartQuickAdd extends DialogComponent {
  requiredRefs = ['dialog', 'form', 'sizes', 'submit'];

  #abortController = null;

  #productRequest = null;

  #cache = new Map();

  #trigger = null;

  #shown = Promise.resolve();

  #product = null;

  #selectedVariantId = null;
  #busy = false;

  #url = '';

  #priceText = '';

  #colour = '';

  #notifyOwned = null;

  #notifyController = null;
  #notifyTimer = 0;

  #notifyFor = { size: '', variantId: '' };

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#onDialogClose, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.refs.form.addEventListener('submit', this.#onSubmit, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#productRequest?.abort();
    this.#productRequest = null;
    this.#cache.clear();
    this.#releaseNotify();
  }

  async openForProduct({
    handle,
    url = '',
    trigger,
    title = '',
    imageSrc = '',
    imageSrcset = '',
    priceHtml = '',
    colour = '',
  }) {
    if (!handle) return false;

    this.#trigger = trigger instanceof HTMLElement ? trigger : null;
    this.#reset();
    this.#url = url;
    this.#paintPlaceholder({ title, url, imageSrc, imageSrcset, priceHtml, colour });
    this.#open();

    const product = await this.#loadProduct(handle);
    if (!product) {
      this.#showError(this.dataset.labelErrorNetwork ?? '');
      return true;
    }

    const shape = readProductShape(product);
    if (!shape) {
      await this.#close();
      return false;
    }

    this.#product = product;
    this.#paintProduct(product, shape);
    return true;
  }

  #open() {
    if (this.refs.dialog.open) return;
    this.showDialog();
    this.#shown = new Promise((resolve) => requestAnimationFrame(() => resolve()));
  }

  async #close() {
    await this.#shown;
    await this.closeDialog();
  }

  #onDialogOpen = () => {
    this.toggleAttribute('open', true);

    this.refs.closeButton?.focus({ preventScroll: true });
  };

  #onDialogClose = () => {
    this.removeAttribute('open');
    this.#productRequest?.abort();
    this.#productRequest = null;

    const trigger = this.#trigger;
    this.#trigger = null;

    if (trigger?.isConnected) {
      trigger.focus({ preventScroll: true });
      if (document.activeElement === trigger) return;
    }

    const drawer = document.getElementById('cart-drawer');
    const fallback = drawer?.querySelector('[data-drawer-close]');
    if (fallback instanceof HTMLElement) fallback.focus({ preventScroll: true });
  };

  #onCartLinesUpdate = () => {
    this.#cache.clear();
  };

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-quick-add-close]')) {
      event.preventDefault();
      this.#close();
      return;
    }

    const size = target.closest('[data-size]');
    if (!(size instanceof HTMLButtonElement)) return;
    event.preventDefault();

    if (size.dataset.available === 'false' && !document.documentElement.classList.contains('bt-wishlist-off')) {
      this.#openNotify(size);
    }
    else this.#select(Number(size.dataset.variantId));
  };

  #onSubmit = async (event) => {
    event.preventDefault();
    if (this.#busy) return;

    const variantId = this.#selectedVariantId;
    if (!variantId) {
      this.#showError(this.dataset.labelChooseSize ?? '');
      const first = this.refs.sizes.querySelector('[data-size][data-available="true"]');
      if (first instanceof HTMLElement) first.focus();
      return;
    }

    this.#setBusy(true);
    this.#showError('');

    try {
      await add([{ id: variantId, quantity: 1 }], { source: this, own: false });
      await this.#close();
    } catch (error) {
      this.#showError(this.#errorMessage(error));
    } finally {
      this.#setBusy(false);
    }
  };

  #reset() {
    this.#product = null;
    this.#selectedVariantId = null;
    this.#url = '';
    this.#priceText = '';
    this.#colour = '';
    this.#showError('');
    this.refs.sizes.replaceChildren();
    this.refs.sizes.hidden = true;
    this.#setBusy(false);
  }

  #paintPlaceholder({ title, url, imageSrc, imageSrcset, priceHtml, colour }) {
    const { image, title: titleNode, price } = this.refs;
    if (titleNode instanceof HTMLElement) titleNode.textContent = title;
    if (price instanceof HTMLElement) this.#paintPrice(priceHtml);
    this.#paintUrl(url);
    this.#colour = (colour || '').trim();
    if (image instanceof HTMLImageElement) {
      image.hidden = !imageSrc;
      if (imageSrcset) image.srcset = imageSrcset;
      else image.removeAttribute('srcset');
      if (imageSrc) image.src = imageSrc;
      else image.removeAttribute('src');
      image.alt = '';
    }
  }

  #paintUrl(url) {
    const { title, mediaLink } = this.refs;
    for (const node of [title, mediaLink]) {
      if (!(node instanceof HTMLAnchorElement)) continue;
      if (url) node.href = url;
      else node.removeAttribute('href');
    }
  }

  #paintPrice(html) {
    const price = this.refs.price;
    if (!(price instanceof HTMLElement)) return;
    price.innerHTML = html;
    const current = price.cloneNode(true);
    for (const struck of  (current).querySelectorAll('s')) struck.remove();
    this.#priceText = (current).textContent?.trim() ?? '';
  }

  #paintProduct(product, shape) {
    if (!shape) return;

    const { title, price, image } = this.refs;
    if (title instanceof HTMLElement && product.title) title.textContent = product.title;
    if (image instanceof HTMLImageElement && !image.getAttribute('src') && product.featured_image) {
      image.src = product.featured_image;
      image.hidden = false;
    }
    if (price instanceof HTMLElement) this.#paintPrice(this.#priceMarkup(product));
    if (!this.#url && product.handle) this.#paintUrl(`${window.Shopify?.routes?.root || '/'}products/${product.handle}`);

    if (!this.#colour && typeof product.title === 'string' && product.title.includes(' in ')) {
      this.#colour = product.title.split(' in ').pop().trim();
    }

    this.#paintSizes(shape);

    const soleId = soleAvailableVariantId(shape);
    if (soleId) this.#select(soleId);
    else if (!shape.hasOptions) {
      this.#selectedVariantId = shape.values[0]?.available ? shape.values[0].variantId : null;
    }
    if (!this.#selectedVariantId && shape.values.every((value) => !value.available)) {
      this.#showError(this.dataset.labelSoldOut ?? '');
    }
  }

  #paintSizes(shape) {
    const { sizes, sizeTemplate } = this.refs;
    sizes.replaceChildren();

    if (!shape.hasOptions || !(sizeTemplate instanceof HTMLTemplateElement)) {
      sizes.hidden = true;
      return;
    }

    const unavailableTemplate = this.dataset.templateSizeUnavailable ?? '';
    const selectTemplate = this.dataset.templateSizeSelect ?? '';

    const nodes = shape.values.map(({ value, variantId, available }) => {
      const node = (sizeTemplate.content.firstElementChild?.cloneNode(true));
      const button = node instanceof HTMLButtonElement ? node : node?.querySelector('button');
      if (!(button instanceof HTMLButtonElement)) return null;

      button.dataset.variantId = String(variantId);
      button.dataset.optionValue = value;
      button.dataset.available = String(available);

      if (available) button.setAttribute('aria-pressed', 'false');
      else button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute(
        'aria-label',
        (available ? selectTemplate : unavailableTemplate).replace('__SIZE__', value)
      );

      const label = button.querySelector('[data-size-label]');
      if (label instanceof HTMLElement) label.textContent = value.toLowerCase();

      const bell = button.querySelector('[data-size-bell]');
      if (bell instanceof HTMLElement) bell.hidden = available;

      return button;
    });

    const rendered = (nodes.filter(Boolean));
    sizes.append(...rendered);
    sizes.hidden = rendered.length === 0;

    if (rendered.length === 1 && rendered[0].dataset.available === 'true') {
      this.#select(Number(rendered[0].dataset.variantId));
    }

    if (rendered.length && rendered.every((button) => button.dataset.available === 'false')) {
      this.#showError(this.dataset.labelSoldOut ?? '');
    }
  }

  #select(variantId) {
    if (!Number.isFinite(variantId) || variantId <= 0) return;
    this.#selectedVariantId = variantId;
    this.#showError('');

    for (const button of this.refs.sizes.querySelectorAll('[data-size]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const selected = Number(button.dataset.variantId) === variantId;
      button.setAttribute('aria-pressed', String(selected));
      button.toggleAttribute('data-selected', selected);
    }
  }

  #setBusy(busy) {
    this.#busy = busy;
    this.refs.submit.disabled = busy;
    this.dataset.state = busy ? 'adding' : 'idle';
  }

  #showError(message) {
    const error = this.refs.error;
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = !message;
  }

  #errorMessage(error) {
    if (error instanceof CartApiError) {
      if (error.code === 'SERVICE_UNAVAILABLE') return this.dataset.labelErrorNetwork ?? '';
      return error.message || this.dataset.labelErrorGeneric || '';
    }
    return this.dataset.labelErrorGeneric ?? '';
  }

  #priceMarkup(product) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'GBP';
    const price = Number(product.price);
    if (!Number.isFinite(price)) return '';

    const compareAt = Number(product.compare_at_price);
    const current = escapeHtml(money(price, format, currency));
    if (!Number.isFinite(compareAt) || compareAt <= price) return current;

    return `<s class="color-text-subdued">${escapeHtml(money(compareAt, format, currency))}</s>${current}`;
  }

  async #openNotify(button) {
    const drawer = (document.querySelector('[data-quick-add-notify], [data-bt-cart-notify]'));
    if (!drawer) return;
    if (typeof drawer.showDialog !== 'function') await customElements.whenDefined('bt-notify-drawer');
    if (typeof drawer.showDialog !== 'function') return;

    this.#notifyFor = { size: button.dataset.optionValue ?? '', variantId: button.dataset.variantId ?? '' };
    this.#fillNotify(drawer);
    this.#claimNotify(drawer);
    drawer.showDialog();
  }

  #fillNotify(drawer) {
    const { size } = this.#notifyFor;
    const title = this.#product?.title || this.refs.title?.textContent?.trim() || '';

    const write = ( selector, value) => {
      const node = drawer.querySelector(selector);
      if (node instanceof HTMLElement) node.textContent = value;
    };
    write('[data-bt-pdp-notify-title]', title);
    write('[data-bt-pdp-notify-price]', this.#priceText);
    write('[data-bt-pdp-notify-size]', size.toLowerCase());

    const sizeWrap = drawer.querySelector('[data-bt-pdp-notify-size-wrap]');
    if (sizeWrap instanceof HTMLElement) sizeWrap.hidden = size === '';

    const image = drawer.querySelector('[data-bt-pdp-notify-image]');
    const source = this.refs.image;
    const src = source instanceof HTMLImageElement ? source.currentSrc || source.src : '';
    if (image instanceof HTMLImageElement) {
      image.hidden = !src;
      if (src) {
        image.src = src;
        image.alt = title;
      } else {
        image.removeAttribute('src');
        image.alt = '';
      }
    }

    this.#setNotifyStatus(drawer, '', '');
    const email = drawer.querySelector('input[type="email"]');
    if (email instanceof HTMLInputElement) this.#clearNotifyValidity(email);
  }

  #claimNotify(drawer) {
    if (this.#notifyOwned === drawer) return;
    this.#releaseNotify();

    this.#notifyOwned = drawer;
    this.#notifyController = new AbortController();
    const { signal } = this.#notifyController;

    drawer.removeAttribute('data-quick-add-notify');
    drawer.setAttribute('data-bt-cart-notify', '');

    drawer.addEventListener('submit', this.#onNotifySubmit, { signal });
    drawer.addEventListener('input', this.#onNotifyInput, { signal });
    drawer.addEventListener(DialogCloseEvent.eventName, () => this.#releaseNotify(), { signal });
  }

  #releaseNotify() {
    const drawer = this.#notifyOwned;
    this.#notifyOwned = null;
    this.#notifyController?.abort();
    this.#notifyController = null;
    window.clearTimeout(this.#notifyTimer);
    if (!drawer) return;

    drawer.removeAttribute('data-bt-cart-notify');
    drawer.setAttribute('data-quick-add-notify', '');
  }

  #onNotifyInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    this.#clearNotifyValidity(input);
    if (this.#notifyOwned) this.#setNotifyStatus(this.#notifyOwned, '', '');
  };

  #onNotifySubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-bt-pdp-notify-form]')) return;
    event.preventDefault();
    this.#submitNotify(form);
  };

  #submitNotify(form) {
    const drawer = this.#notifyOwned;
    if (!drawer) return;

    const input = form.querySelector('input[type="email"]');
    const submit = form.querySelector('button[type="submit"]');

    if (!(input instanceof HTMLInputElement) || !input.checkValidity()) {
      const message = this.dataset.labelNotifyInvalid ?? '';
      if (input instanceof HTMLInputElement) {
        input.setAttribute('aria-invalid', 'true');
        input.closest('.bt-newsletter-field')?.classList.add('bt-newsletter-field--invalid');
        input.focus();
      }
      this.#setNotifyStatus(drawer, message, 'error');
      return;
    }

    this.#clearNotifyValidity(input);
    if (submit instanceof HTMLButtonElement) submit.disabled = true;
    this.#setNotifyStatus(drawer, this.dataset.labelNotifySending ?? '', 'loading');

    let settled = false;
    const finish = ( ok, message) => {
      if (settled) return;
      settled = true;
      if (submit instanceof HTMLButtonElement) submit.disabled = false;
      const fallback = ok ? this.dataset.labelNotifySuccess : this.dataset.labelNotifyError;
      this.#setNotifyStatus(drawer, message || fallback || '', ok ? 'success' : 'error');
    };

    form.dispatchEvent(
      new CustomEvent('bt-pdp:stock-notify', {
        bubbles: true,
        cancelable: true,
        detail: {
          email: input.value.trim(),
          productId: this.#product?.id ? String(this.#product.id) : '',
          productHandle: this.#product?.handle || '',
          variantId: this.#notifyFor.variantId,
          colour: this.#colour,
          size: this.#notifyFor.size,
          setResult: ( status, message) =>
            finish(status === 'success', message),
        },
      })
    );

    window.clearTimeout(this.#notifyTimer);
    this.#notifyTimer = window.setTimeout(() => {
      if (!settled) finish(true, '');
    }, NOTIFY_OPTIMISTIC_MS);
  }

  #clearNotifyValidity(input) {
    input.setCustomValidity('');
    input.removeAttribute('aria-invalid');
    input.closest('.bt-newsletter-field')?.classList.remove('bt-newsletter-field--invalid');
  }

  #setNotifyStatus(drawer, message, kind) {
    const status = drawer.querySelector('[data-bt-pdp-notify-status]');
    if (!(status instanceof HTMLElement)) return;
    status.hidden = !message;
    status.textContent = message;
    status.dataset.state = kind;
  }

  async #loadProduct(handle) {
    const cached = this.#cache.get(handle);
    if (cached) return cached;

    this.#productRequest?.abort();
    this.#productRequest = new AbortController();

    const root = window.Shopify?.routes?.root || '/';
    try {
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: this.#productRequest.signal,
      });
      if (!response.ok) return null;

      const product = await response.json();
      this.#cache.set(handle, product);
      return product;
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[bt-cart-quick-add] Product fetch failed', error);
      return null;
    }
  }
}

/**
 * @param {{ values?: Array<{ variantId: number, available: boolean }> } | null} shape
 * @returns {number | null}
 */
function soleAvailableVariantId(shape) {
  const values = shape?.values || [];
  if (values.length !== 1) return null;
  const only = values[0];
  if (!only?.available || !Number.isFinite(only.variantId) || only.variantId <= 0) return null;
  return only.variantId;
}

function readProductShape(product) {
  const options = Array.isArray(product?.options) ? product.options : [];
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  if (!variants.length) return null;

  const names = options.map((option) => String(option?.name ?? option ?? '').trim().toLowerCase());
  const single = variants.length === 1 && String(variants[0]?.title ?? '').trim().toLowerCase() === DEFAULT_OPTION_VALUE;

  if (single || names.length === 0) {
    return {
      hasOptions: false,
      values: [{ value: '', variantId: Number(variants[0].id), available: Boolean(variants[0].available) }],
    };
  }

  if (names.length > 1) return null;

  return {
    hasOptions: true,
    values: variants.map((variant) => ({
      value: String(variant.options?.[0] ?? variant.title ?? '').trim(),
      variantId: Number(variant.id),
      available: Boolean(variant.available),
    })),
  };
}

function money(minorUnits, format, currency) {
  const iso = String(currency).toUpperCase();
  const scaled = iso === 'JPY' ? minorUnits / 100 : minorUnits;
  return formatMoney(scaled, format, currency).replace(/([.,])00(?=\D*$)/, '');
}

function escapeHtml(value) {
  const element = document.createElement('span');
  element.textContent = value;
  return element.innerHTML;
}

if (!customElements.get('bt-cart-quick-add')) {
  customElements.define('bt-cart-quick-add', BtCartQuickAdd);
}
